import { requestApi } from "../api/client.js";

export interface PlanCardContent {
  applicability_question: string | null;
  category: string;
  code: string;
  default_applicable: boolean;
  display_order: number;
  id: string;
  procedure_requirement: string;
  records: {
    mpi_template_name: string | null;
    notes: string | null;
    record_requirement: string;
    record_type_code: string;
  }[];
  summary_md: string | null;
  title: string;
}

export interface PlanPack {
  authority: string;
  cards: PlanCardContent[];
  code: string;
  effective_from: string | null;
  id: string;
  jurisdiction: string;
  published_on: string | null;
  source_url: string | null;
  status: "active" | "draft" | "retired";
  title: string;
  version_label: string;
}

export interface ContentPacksResponse {
  packs: PlanPack[];
  version: number;
}

export interface PlanInstanceCard {
  applicable: boolean;
  card_code: string;
  reason: string | null;
  title: string;
}

export interface PlanInstance {
  answers: Record<string, unknown>;
  cards: PlanInstanceCard[];
  effective_from: string | null;
  effective_to: string | null;
  id: string;
  location_id: string;
  plan_pack_code: string;
  published_at: string | null;
  state: "draft" | "published" | "superseded";
  version_no: number;
}

export interface PlanPublishResponse {
  generated_task_definition_ids: string[];
  plan_instance: PlanInstance;
}

export interface GeneratedTaskDefinition {
  id: string;
  origin: "manager" | "plan_generated";
  record_type_code: string | null;
  schedule: {
    due_window_end: string | null;
    due_window_start: string | null;
    one_off_due_at: string | null;
    rrule: string | null;
  } | null;
  status: "active" | "archived" | "paused";
  task_kind: string;
  title: string;
}

export function getContentPacks(): Promise<ContentPacksResponse> {
  return requestApi<ContentPacksResponse>("/content/packs");
}

export function listPlanInstances(locationId: string): Promise<PlanInstance[]> {
  return requestApi<PlanInstance[]>(
    `/plan-instances?location_id=${encodeURIComponent(locationId)}`,
  );
}

export function createPlanDraft(input: {
  clone_current: boolean;
  location_id: string;
  plan_pack_code: string;
}): Promise<PlanInstance> {
  return requestApi<PlanInstance>("/plan-instances", {
    body: JSON.stringify(input),
    headers: { "Idempotency-Key": crypto.randomUUID() },
    method: "POST",
  });
}

export function updatePlanCard(
  planInstanceId: string,
  card: { applicable: boolean; card_code: string; reason?: string | null },
): Promise<PlanInstance> {
  return requestApi<PlanInstance>(`/plan-instances/${planInstanceId}`, {
    body: JSON.stringify({ cards: [card] }),
    method: "PATCH",
  });
}

export function publishPlan(planInstanceId: string): Promise<PlanPublishResponse> {
  return requestApi<PlanPublishResponse>(
    `/plan-instances/${planInstanceId}/publish`,
    {
      headers: { "Idempotency-Key": crypto.randomUUID() },
      method: "POST",
    },
  );
}

export async function listGeneratedTasks(
  locationId: string,
): Promise<GeneratedTaskDefinition[]> {
  const page = await requestApi<{
    items: GeneratedTaskDefinition[];
    next_cursor: string | null;
  }>(`/task-definitions?location_id=${encodeURIComponent(locationId)}&limit=200`);
  return page.items.filter((task) => task.origin === "plan_generated");
}

export function decisionProgress(pack: PlanPack, instance: PlanInstance): {
  answered: number;
  questions: number;
} {
  const questionCodes = new Set(
    pack.cards
      .filter((card) => card.applicability_question !== null)
      .map((card) => card.code),
  );
  return {
    answered: instance.cards.filter((card) => questionCodes.has(card.card_code))
      .length,
    questions: questionCodes.size,
  };
}

export function recurrenceLabel(rrule: string | null | undefined): string {
  if (!rrule) return "One-off schedule";
  const frequency = /FREQ=([^;]+)/.exec(rrule)?.[1]?.toLowerCase();
  const interval = /INTERVAL=(\d+)/.exec(rrule)?.[1];
  if (!frequency) return "Recurring schedule";
  if (interval && interval !== "1") return `Every ${interval} ${frequency.replace(/ly$/, "")}s`;
  return frequency === "daily" ? "Every day" : frequency === "weekly" ? "Every week" : frequency === "monthly" ? "Every month" : `Every ${frequency.replace(/ly$/, "")}`;
}
